import { useState, useEffect, useCallback } from 'react';

export function usePortsData() {
  const [portsData, setPortsData] = useState([]);
  const [countriesData, setCountriesData] = useState([]);
  const [continentsData, setContinentsData] = useState([]);
  const [regionsData, setRegionsData] = useState([]);
  const [portRegionsData, setPortRegionsData] = useState([]);
  const [timeZonesData, setTimeZonesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    const sessionId = sessionStorage.getItem('sessionId');
    
    // Skip loading if no session
    if (!sessionId) {
      setLoading(false);
      setCountriesData([]);
      setContinentsData([]);
      setPortsData([]);
      setRegionsData([]);
      setPortRegionsData([]);
      setTimeZonesData([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const headers = {
        'x-session-token': sessionId
      };

      // Fetch all data in parallel
      const [countriesRes, continentsRes, portsRes, regionsRes, portRegionsRes, timeZonesRes] = await Promise.all([
        fetch('/api/countries', { headers }),
        fetch('/api/continents', { headers }),
        fetch('/api/ports', { headers }),
        fetch('/api/regions', { headers }),
        fetch('/api/port-regions', { headers }),
        fetch('/api/timezones', { headers })
      ]);

      // Check for authentication errors
      if (countriesRes.status === 401 || portsRes.status === 401) {
        sessionStorage.removeItem('sessionId');
        sessionStorage.removeItem('connectionInfo');
        window.location.reload();
        return;
      }

      // Parse responses
      const countries = await countriesRes.json();
      const continents = await continentsRes.json();
      const ports = await portsRes.json();
      const regions = await regionsRes.json();
      const portRegions = await portRegionsRes.json();
      const timeZones = await timeZonesRes.json();

      // Check for errors in responses
      if (countries.error) throw new Error(countries.error);
      if (continents.error) throw new Error(continents.error);
      if (ports.error) throw new Error(ports.error);
      if (regions.error) throw new Error(regions.error);
      if (portRegions.error) throw new Error(portRegions.error);
      if (timeZones.error) throw new Error(timeZones.error);

      setCountriesData(countries);
      setContinentsData(continents);
      setPortsData(ports);
      setRegionsData(regions);
      setPortRegionsData(portRegions);
      setTimeZonesData(timeZones);
      setLoading(false);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refetch = useCallback(() => {
    return loadData();
  }, [loadData]);

  return { portsData, countriesData, continentsData, regionsData, portRegionsData, timeZonesData, loading, error, refetch };
}
